<template>

    <Toast />
    <section class="p-5 bg-echoes-background-black">
        
        <UsersHeader>
            <template #title>System Settings</template>
            <template #description>
                Manage and organize your system settings
            </template>
        </UsersHeader>
     
        <div class="bg-echoes-background-gray shadow-sm rounded-xl border border-echoes-border-light overflow-hidden mb-8">
            <div class="p-4">
                <div class="flex flex-col sm:flex-row gap-4 items-center justify-between">
                    <div class="w-full sm:w-96">
                        <TextInput 
                            v-model="searchQuery" 
                            placeholder="Search system settings..." 
                            class="w-full"
                        />
                    </div>
                </div>
            </div>
            
            <div>
                <table class="min-w-full divide-y divide-echoes-border-light">
                    <thead class="bg-echoes-border-light">
                        <tr class="border-b border-echoes-border-light">
                            <th class="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                                Name
                            </th>
                            <th class="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                                Value
                            </th>
                            <th class="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody class="bg-echoes-background-gray divide-y divide-echoes-border-light">
                        <template v-for="systemSetting in filteredSystemSettings" :key="systemSetting.id">
                            <tr class="bg-echoes-background-black/80 hover:bg-echoes-background-black/50 transition-colors duration-200">
                                <td class="px-6 py-4 text-sm font-medium text-white">
                                    {{ systemSetting.name }}
                                </td>
                                <td class="px-6 py-4 text-sm text-white">
                                    <span :title="systemSetting.value" class="inline-flex px-3 py-0.5 rounded-full text-sm font-medium bg-echoes-background-black border border-echoes-border-light text-white/80">
                                        {{ reduceValue(systemSetting.value) }}
                                    </span>
                                </td>
                                <td class="px-6 py-4 text-sm font-medium">
                                    <button 
                                    @click="editSystemSetting(systemSetting)"
                                    :disabled="systemSetting.blocked"
                                    :class="systemSetting.blocked ? 'bg-white/10 cursor-not-allowed text-white/50 relative' : 'bg-echoes-background-black/60 hover:bg-white/5 transition-colors duration-200 text-white'"
                                        class="inline-flex items-center px-3 py-2 text-sm font-medium rounded-xl border border-echoes-border-light"
                                    >
                                        <i :class="systemSetting.blocked ? 'mdi mdi-lock-open-outline' : 'mdi mdi-pencil'"></i> Edit
                                        <span v-if="systemSetting.blocked" class="tooltip">{{ systemSetting.description }}</span>
                                    </button>
                                    
                                </td>
                            </tr>
                        </template>
                    </tbody>
                </table>
            </div>
        </div>

        <!-- Modal -->
        <div v-if="showModal" class="fixed inset-0 z-50 overflow-y-auto">
            <div class="flex items-center justify-center min-h-screen px-4">
                <div class="fixed inset-0 bg-black bg-opacity-50 transition-opacity backdrop-blur-sm" @click="closeModal"></div>
                
                <div class="relative bg-echoes-background-gray rounded-xl shadow-xl max-w-md w-full border border-echoes-border-light">
                    <div class="flex justify-between items-center px-6 py-4 border-b border-echoes-border-light">
                        <h2 class="text-xl font-semibold text-white">
                            Edit {{ editedSystemSetting.name }}
                        </h2>
                        <button @click="closeModal" class="text-white/60 bg-transparent hover:bg-echoes-background-black hover:text-white rounded-lg text-sm p-1.5 ms-auto inline-flex items-center transition-colors duration-200">
                            <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"></path>
                            </svg>
                        </button>
                    </div>
                    <form @submit.prevent="saveSystemSetting" class="p-6 space-y-4">
                        <div class="flex flex-col">
                                    <TextInput v-if="editedSystemSetting.type === 'text'" 
                                        :id="editedSystemSetting.name" 
                                        v-model="editedSystemSetting.value" 
                                        class="w-full" 
                                        :required="true" 
                                        placeholder="Enter setting value"
                                    />
                                    <TextInput v-if="editedSystemSetting.type === 'number' || editedSystemSetting.type === 'float'" 
                                        :id="editedSystemSetting.name" 
                                        v-model="editedSystemSetting.value" 
                                        class="w-full" 
                                        :required="true" 
                                        placeholder="Enter setting value"
                                        type="number"
                                        :min="0"
                                        :max="editedSystemSetting.name === 'default_fee' ? 99.99 : null"
                                        :step="editedSystemSetting.type === 'number' ? 1 : 0.01"
                                    />

                                    
                                    <SelectInput v-if="editedSystemSetting.type === 'boolean'" 
                                        :options="booleanOptions"
                                        placeholder="Select a value"
                                        v-model="editedSystemSetting.value"
                                        class="w-full"
                                    />
                            
                            <div class="flex items-center gap-2 p-2 bg-echoes-background-black rounded-lg mt-5">
                                <i class="mdi mdi-information-outline text-sm text-white/60"></i>
                                <span class="text-xs text-white/60">
                                    {{editedSystemSetting.description}}
                                </span>
                            </div>
                        </div>

                        <div class="flex justify-end gap-3 mt-6">
                            <button 
                                type="button"
                                @click="closeModal"
                                class="btn rounded-full bg-echoes-background-black text-white/80 hover:bg-echoes-background-black/80 border-transparent px-4 py-1.5 text-sm transition-colors duration-200"
                            >
                                Cancel
                            </button>
                            <button 
                                type="submit"
                                class="btn rounded-full text-white bg-echoes-primary hover:bg-echoes-primary/80 border-transparent px-4 py-1.5 text-sm transition-colors duration-200"
                            >
                                Save Changes
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import axios from '@/plugins/axios';
import { useToast } from '@/composables/use-toast';
import { useLayoutComponents } from '@/composables/layout.js';

import { useStorage } from '@/composables/use-storage.js';
import UsersHeader from '@/pages/admin/AdminHeader.vue';

const { SelectInput, TextInput, Toast } = useLayoutComponents();
const { addToast } = useToast();
const { saveSystemSettings } = useStorage();

const systemSettings = ref([]);
const showModal = ref(false);
const searchQuery = ref('');
const isEditing = ref(false);
const editedSystemSetting = ref({});
const editingSystemSettingId = ref(null);

const booleanOptions = [
    { label: 'True', value: "true" },
    { label: 'False', value: "false" },
];

const filteredSystemSettings = computed(() => {
    return systemSettings.value.filter(systemSetting => {
        const matchesSearch = systemSetting.name.toLowerCase().includes(searchQuery.value.toLowerCase());
        return matchesSearch;
    });
});

const fetchSystemSettings = async () => {
    try {
        const response = await axios.get('/api/system-settings');
        if(response.status === 200) {
            systemSettings.value = response.data;
        } else {
            console.error('Error fetching system settings:', response.data);
            addToast('Error fetching system settings', 'error');
        }
    } catch (error) {
        console.error('Error fetching system settings:', error);
        addToast('Error fetching system settings', 'error');
    }
};

const fetchPublicSystemSettings = async () => { 
    axios.get('/api/system-settings/public-settings').then((res) => {
        saveSystemSettings(res.data);
    });
};

const editSystemSetting = (systemSetting) => {
    isEditing.value = true;
    editingSystemSettingId.value = systemSetting.id;
    editedSystemSetting.value = systemSetting;
    showModal.value = true;
};

const closeModal = () => {
    showModal.value = false;
    isEditing.value = false;
    editingSystemSettingId.value = null;
    editedSystemSetting.value = {
        name: '',
        value: ''
    };
};

const reduceValue = (value) => {
    return value.substring(0, 32) + (value.length > 32 ? '...' : '');
}

const saveSystemSetting = async () => {
    try {
        if(editedSystemSetting.value.name === 'default_fee' && Number(editedSystemSetting.value.value) > 99.99 || Number(editedSystemSetting.value.value) < 0) {
            addToast('Default fee must be between 0 and 99.99', 'error');
            return;
        }
        const response = await axios.post(`/api/system-settings/${editingSystemSettingId.value}`, editedSystemSetting.value.value);
        if(response.status === 201) {
            addToast('System setting saved successfully', 'success');
            await fetchSystemSettings();
            await fetchPublicSystemSettings();
            closeModal();
        } else {
            console.error('Error saving system setting:', response.data);
            addToast('Error saving system setting', 'error');
        }
    } catch (error) {
        console.error('Error saving system setting:', error);
        addToast('Error saving system setting', 'error');
    }
};

onMounted(async () => {
    await fetchSystemSettings();
});
</script>

<style scoped lang="postcss">
.btn {
    @apply inline-flex items-center justify-center font-medium transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-echoes-primary disabled:opacity-50 disabled:cursor-not-allowed;
}

.table-container thead {
    @apply sticky top-0 z-10;
}

.table-container tbody {
    @apply relative;
}

.table-container::-webkit-scrollbar {
    width: 8px;
    height: 8px;
}

.table-container::-webkit-scrollbar-track {
    @apply bg-echoes-background-black rounded-full;
}

.table-container::-webkit-scrollbar-thumb {
    @apply bg-echoes-background-gray rounded-full transition-colors duration-200;
}

.table-container::-webkit-scrollbar-thumb:hover {
    @apply bg-echoes-background-gray/80;
}

/* Animations */
@keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
}

.table-container tr {
    animation: fadeIn 0.3s ease-in-out;
}

.tooltip {
    @apply absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-3 py-2 text-xs font-semibold text-white/80 bg-echoes-background-black rounded-lg whitespace-normal w-max max-w-[300px];
    visibility: hidden;
    opacity: 0;
    transition: all 0.2s ease-in-out;
    z-index: 50;
}

.tooltip::after {
    content: '';
    @apply absolute left-1/2 -translate-x-1/2 -bottom-1 border-4 border-transparent border-t-gray-900;
}

button:disabled:hover .tooltip {
    visibility: visible;
    opacity: 1;
}
</style>